/**
 * Hook to check tier-specific feature availability
 * Uses subscription tier and fetches feature flags from Firebase config.
 * Fail-safe: if subscription/tier never resolve or take too long, we resolve with free-tier defaults so the app never hangs.
 */

import { useState, useEffect, useRef } from 'react';
import { useSubscription } from './useSubscription';
import { getTierFeatures, getDefaultTierFeatures, getAllTierFeatures as getAllTierFeaturesUtil, TierFeatures, TierFeaturesConfig } from '../utils/tierFeatures';

const TIER_LOAD_TIMEOUT_MS = 8000;

export const useTierFeatures = () => {
  const { subscriptionTier, loading: subscriptionLoading } = useSubscription();
  const [features, setFeatures] = useState<TierFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    resolvedRef.current = false;

    const applyFreeFallback = () => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      try {
        const defaults = getDefaultTierFeatures();
        setFeatures(defaults.free);
        setError(null);
      } catch (e) {
        console.warn('Tier features: free fallback failed', e);
      }
      setLoading(false);
    };

    const loadFeatures = async () => {
      // Fail-safe: subscription finished but no tier (e.g. legacy user doc) → treat as free and stop loading
      if (!subscriptionLoading && !subscriptionTier) {
        applyFreeFallback();
        return;
      }

      if (subscriptionLoading || !subscriptionTier) {
        setLoading(true);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const tierFeatures = await getTierFeatures(subscriptionTier);
        if (!resolvedRef.current) {
          resolvedRef.current = true;
          setFeatures(tierFeatures);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading tier features:', err);
        setError(err instanceof Error ? err.message : 'Failed to load features');
        try {
          const defaults = getDefaultTierFeatures();
          if (!resolvedRef.current) {
            resolvedRef.current = true;
            setFeatures(defaults[subscriptionTier] || defaults.free);
            setLoading(false);
          }
        } catch (fallbackErr) {
          console.error('Error loading default features:', fallbackErr);
          applyFreeFallback();
        }
      } finally {
        if (!resolvedRef.current) setLoading(false);
      }
    };

    loadFeatures();

    // Fail-safe: if still loading after timeout (e.g. Firebase hang), resolve with free tier so UI never blocks
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      if (resolvedRef.current) return;
      console.warn('Tier features: load timeout, using free tier fallback');
      applyFreeFallback();
    }, TIER_LOAD_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [subscriptionTier, subscriptionLoading]);

  // Helper functions for common feature checks
  const canUseFeature = (feature: keyof TierFeatures): boolean => {
    if (!features) return false;
    return features[feature] === true;
  };

  // Core feature helpers
  const canUseAnalytics = () => canUseFeature('analytics');
  const canUseApprovalSystem = () => canUseFeature('approvalSystem');
  const canUseLandingPageBuilder = () => canUseFeature('landingPageBuilder');
  const canUseAllQuestionTypes = () => canUseFeature('allQuestionTypes');
  const canUseCustomBranding = () => canUseFeature('customBranding');
  const canUsePrioritySupport = () => canUseFeature('prioritySupport');
  const canUseMultiLevelApproval = () => canUseFeature('multiLevelApproval');
  const canUsePdfExport = () => canUseFeature('pdfExport');
  const canUseExcelExport = () => canUseFeature('excelExport');

  // Platform feature helpers
  const canUseFormThemes = () => canUseFeature('formThemes');
  const canUsePublicFormSharing = () => canUseFeature('publicFormSharing');
  const canUseMobileApp = () => canUseFeature('mobileApp');
  const canUseOfflineMode = () => canUseFeature('offlineMode');
  const canUseSignatures = () => canUseFeature('signatures');
  const canUseImageCapture = () => canUseFeature('imageCapture');
  const canUseImageAnnotations = () => canUseFeature('imageAnnotations');
  const canUseEmailNotifications = () => canUseFeature('emailNotifications');

  // Functional feature helpers
  const canUseConditionalLogic = () => canUseFeature('conditionalLogic');
  const canUseEditSubmissions = () => canUseFeature('editSubmissions');
  const canUseReminders = () => canUseFeature('reminders');
  const canUseCalendarView = () => canUseFeature('calendarView');
  const canUseCustomSlug = () => canUseFeature('customSlug');
  const canUseCompanyAccount = () => canUseFeature('companyAccount');
  const canUsePasswordProtectedPages = () => canUseFeature('passwordProtectedPages');
  const canUseQuestionTemplates = () => canUseFeature('questionTemplates');
  const canUseDistributionGroups = () => canUseFeature('distributionGroups');

  // Function to get all tier features (for comparison charts, etc.)
  const getAllTierFeatures = async (): Promise<TierFeaturesConfig> => {
    return await getAllTierFeaturesUtil();
  };

  return {
    features,
    loading: loading || subscriptionLoading,
    error,
    // Helper functions
    canUseFeature,
    canUseAnalytics,
    canUseApprovalSystem,
    canUseLandingPageBuilder,
    canUseAllQuestionTypes,
    canUseCustomBranding,
    canUsePrioritySupport,
    canUseMultiLevelApproval,
    canUsePdfExport,
    canUseExcelExport,
    canUseFormThemes,
    canUsePublicFormSharing,
    canUseMobileApp,
    canUseOfflineMode,
    canUseSignatures,
    canUseImageCapture,
    canUseImageAnnotations,
    canUseEmailNotifications,
    canUseConditionalLogic,
    canUseEditSubmissions,
    canUseReminders,
    canUseCalendarView,
    canUseCustomSlug,
    canUseCompanyAccount,
    canUsePasswordProtectedPages,
    canUseQuestionTemplates,
    canUseDistributionGroups,
    // Function to get all tier features
    getAllTierFeatures,
    // Direct feature checks
    hasAnalytics: features?.analytics ?? false,
    hasApprovalSystem: features?.approvalSystem ?? false,
    hasLandingPageBuilder: features?.landingPageBuilder ?? false,
    hasAllQuestionTypes: features?.allQuestionTypes ?? false,
    hasCustomBranding: features?.customBranding ?? false,
    hasPrioritySupport: features?.prioritySupport ?? false,
    hasMultiLevelApproval: features?.multiLevelApproval ?? false,
    hasPdfExport: features?.pdfExport ?? false,
    hasExcelExport: features?.excelExport ?? false,
    hasFormThemes: features?.formThemes ?? false,
    hasPublicFormSharing: features?.publicFormSharing ?? false,
    hasMobileApp: features?.mobileApp ?? false,
    hasOfflineMode: features?.offlineMode ?? false,
    hasSignatures: features?.signatures ?? false,
    hasImageCapture: features?.imageCapture ?? false,
    hasImageAnnotations: features?.imageAnnotations ?? false,
    hasEmailNotifications: features?.emailNotifications ?? false,
    hasConditionalLogic: features?.conditionalLogic ?? false,
    hasEditSubmissions: features?.editSubmissions ?? false,
    hasReminders: features?.reminders ?? false,
    hasCalendarView: features?.calendarView ?? false,
    hasCustomSlug: features?.customSlug ?? false,
    hasCompanyAccount: features?.companyAccount ?? false,
    hasPasswordProtectedPages: features?.passwordProtectedPages ?? false,
    hasQuestionTemplates: features?.questionTemplates ?? false,
    hasDistributionGroups: features?.distributionGroups ?? false,
  };
};
